import axios, {AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse} from 'axios';
import {isEmpty} from './JSHelper';
import {getStorageItem, StorageKeys} from './StorageHelper';

enum StatusCode {
  Unauthorized = 401,
  Forbidden = 403,
  TooManyRequests = 429,
  InternalServerError = 500,
  TimedOut = 499,
  UnknownError = 498,
}

const StatusCodeToErrorMessageMap: Record<number, string> = {
  400: "Bad request",
  401: "Access was denied",
  403: "Request forbidden",
  404: "The requested resource was not found.",
  429: "Too many requests. Please try again later",
  499: "Request timed out",
};

const jsonHeaders: Readonly<Record<string, string | boolean>> = {
  Accept: "application/json",
  "Content-Type": "application/json; charset=utf-8",
  "Access-Control-Allow-Credentials": true,
  "X-Requested-With": "XMLHttpRequest",
};

// We can use the following function to inject the JWT token through an interceptor
// We get the `accessToken` from the localStorage that we set when we authenticate
// eslint-disable-next-line import/prefer-default-export
export class Http {
  private instance: AxiosInstance | null = null;

  private static payoutApiClient: Http;

  constructor(private baseURL: string, private needsJwtToken: boolean = false, private debugMode: boolean = false) {}

  private get http(): AxiosInstance {
    return this.instance ?? this.initHttp();
  }

  static createPayoutApiClient(): Http {
    if (isEmpty(this.payoutApiClient)) {
      this.payoutApiClient = new Http(`${process.env.REACT_APP_PAYOUT_BASE_URL}`, false, true);
    }
    return this.payoutApiClient;
  }

  initHttp() {
    const http = axios.create({
      baseURL: this.baseURL,
      headers: jsonHeaders,
      withCredentials: true,
    });

    http.interceptors.request.use(
      async (config: any): Promise<any> => {
        if (this.debugMode) {
          console.info(`Sending request to ${config.baseURL}${config.url}`);
        }
        return config;
      },
      (error) => this.handleError(error)
    );

    http.interceptors.request.use((config) => {
      config.headers.Authorization = 'Bearer ' + getStorageItem(StorageKeys.AUTH_JWT_ACCESS_TOKEN);
      return config;
    });

    http.interceptors.response.use(
      (response) => response,
      (error) => this.handleError(error)
    );

    this.instance = http;
    return http;
  }

  request<T = any, R = AxiosResponse<T>>(config: AxiosRequestConfig): Promise<R> {
    return this.http.request(config);
  }

  get<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
    return this.http.get<T, R>(url, config);
  }

  post<T = any, R = AxiosResponse<T>>(url: string, data?: T, config?: AxiosRequestConfig): Promise<R> {
    return this.http.post<T, R>(url, data, config);
  }

  postXml<T = any, R = AxiosResponse<T>>(data?: T, config?: AxiosRequestConfig): Promise<R> {
    const finalConfig: any = config ?? {headers: {}};
    finalConfig.headers["Content-Type"] = "text/xml";
    return this.http.post<T, R>("/", data, finalConfig);
  }

  put<T = any, R = AxiosResponse<T>>(url: string, data?: T, config?: AxiosRequestConfig): Promise<R> {
    return this.http.put<T, R>(url, data, config);
  }

  delete<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
    return this.http.delete<T, R>(url, config);
  }

  // Handle global app errors
  // We can handle generic app errors depending on the status code

  private handleError = (error: AxiosError) => {
    if (error.response) {
      let apiResponse: any = error.response.data;
      if (apiResponse?.message) {
        return Promise.reject(apiResponse.message);
      }

      const parsedError = error.response ?? {status: StatusCode.UnknownError, data: "Unknown network error"};
      const {status, data} = parsedError;
      return Promise.reject(`(${status}) - ${StatusCodeToErrorMessageMap[status] ?? "Unknown network error"} - ${JSON.stringify(data)}`);
    }

    if (error.code === "ECONNREFUSED") {
      const destination = error?.request?._currentUrl ?? "-";
      return Promise.reject(`Connection refused to ${destination}`);
    }

    if (error.request) {
      return Promise.reject(`(-) Network error failed to ${error.request._currentUrl}`);
    }

    console.log(`Failed to process Axios request to with error: ${error.toJSON()} with config; ${error.config}`);

    return Promise.reject(`Unknown application error: ${error.toJSON()}`);
  };
}

